import React, { type FC, useState, useEffect } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import InlineDialog from '@atlaskit/inline-dialog';
import InlineEdit, { type InlineEditProps } from '@atlaskit/inline-edit';
import Textfield, { type TextFieldProps } from '@atlaskit/textfield';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import messages from '../messages';
import { InlineEditContainer } from '../styled';
import type { Field } from '../types';
import { FIELD_MAX_LENGTH, FIELD_MAX_LENGTH_OLD } from './constants';
import { TextFieldReadView } from './read-view';

export const TextField: FC<
	Field &
		Partial<Pick<InlineEditProps<string>, 'validate' | 'readView'>> &
		Omit<TextFieldProps, 'onChange' | 'value'>
> = ({
	id,
	label,
	value,
	onChange,
	validate,
	readView,
	isInline,
	getValueContainer,
	...inputProps
}) => {
	const { formatMessage } = useIntl();
	const [currentValue, setCurrentValue] = useState(value || '');
	const valueForAriaLabel = value || formatMessage(messages.noValue);

	const onConfirmWithChange = (newValue: string, analyticsEvent: UIAnalyticsEvent) => {
		const newValueTrimmed = newValue.trim();

		if (value === null && newValueTrimmed === '') return;
		if (newValueTrimmed === value) return;

		onChange({ value: newValueTrimmed }, analyticsEvent);
	};

	useEffect(() => {
		setCurrentValue(() => value || '');
	}, [value]);

	return (
		<InlineEditContainer>
			<InlineEdit<string>
				defaultValue={undefined}
				editButtonLabel={formatMessage(messages.editButtonLabel, {
					label,
					value: valueForAriaLabel,
				})}
				validate={validate}
				editView={({ errorMessage, ...fieldProps }) => (
					<>
						<InlineDialog isOpen={fieldProps.isInvalid} content={errorMessage} placement="right">
							<Textfield
								type="text"
								{...fieldProps}
								id={id}
								value={currentValue}
								onChange={(event) => {
									const newValue = event.currentTarget.value;
									setCurrentValue(() => newValue);
									fieldProps.onChange(newValue);
								}}
								onBlur={() => {
									setCurrentValue(() => currentValue);
									fieldProps.onChange(currentValue);
									fieldProps.onBlur();
								}}
								autoFocus
								isCompact
								maxLength={
									ff('jsm-csm-public-api-prerequisite-improvements_4obdb')
										? FIELD_MAX_LENGTH
										: FIELD_MAX_LENGTH_OLD
								}
								elemAfterInput={
									fieldProps.isInvalid && (
										<IconWrapper>
											<ErrorIcon label="" primaryColor={token('color.icon.danger', colors.R400)} />
										</IconWrapper>
									)
								}
								testId="servicedesk-customer-service-custom-details.common.ui.field.text-field"
								{...inputProps}
							/>
						</InlineDialog>
					</>
				)}
				readView={
					readView ||
					(() => (
						<TextFieldReadView value={value} getValueContainer={getValueContainer} inInlineEdit />
					))
				}
				onConfirm={onConfirmWithChange}
				readViewFitContainerWidth
			/>
		</InlineEditContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	paddingRight: token('space.075', '6px'),
	lineHeight: '100%',
});

export { TextFieldReadView };
